import WorkOrderDeleteDialog from "./WorkOrderDeleteDialog";
import {
	Broadcastable,
	Crudable,
	FetchesData
} from "kinnect-mixins";
import { FacilityMaintenanceWorkOrder } from "kinnect-models";
import {
	KActionDialog,
	KDataTable,
	KDataTableMenu,
	KStatusStepper
} from "kinnect-general";
import {
	VBtn,
	VCard,
	VCol,
	VContainer,
	VIcon,
	VRow
} from "vuetify/lib";
import { formatCurrency } from "kinnect-formatters";
import { removeByObjectValue } from "kinnect-helpers";

export default KDataTable.extend( {
	name: "FacilityMaintenanceWorkOrdersTable",

	mixins: [
		Broadcastable,
		Crudable,
		FetchesData
	],

	props: {
		itemsFilter: {
			default: item => item,
			type: Function
		},

		modifyItemsQuery: {
			default: query => query,
			type: Function
		},

		noDataText: {
			...KDataTable.options.props.noDataText,
			default: "There are no work orders to display"
		},

		noResultsText: {
			...KDataTable.options.props.noResultsText,
			default: "No matching work orders found"
		},

		sortBy: {
			...KDataTable.options.props.sortBy,
			default: "name"
		}
	},

	data: () => ( {
		items: [],
		broadcasts: {
			Model: FacilityMaintenanceWorkOrder,
			channel: "work-order",
			propKey: "items",
			eventNames: {
				c: "work-order.created",
				u: "work-order.updated",
				d: "work-order.deleted"
			}
		},
		crud: "facility_maintenance_work_orders",
		statuses: [
			{
				text: "Idle",
				value: "Idle"
			},
			{
				text: "In Progress",
				value: "In Progress"
			},
			{
				text: "Completed",
				value: "Completed"
			}
		]
	} ),

	computed: {
		filteredItems() {
			return this.items.filter( this.itemsFilter );
		},

		defaultHeaders() {
			return [
				{
					text: "Status",
					value: "$.status"
				},
				{
					text: "Location",
					value: "location.name"
				},
				{
					text: "Vendor",
					value: "vendor.nameWithType"
				},
				{
					text: "Cost",
					value: "cost"
				},
				{
					text: "Material Cost",
					value: "materialCost"
				},
				{
					text: "Material Cost Notes",
					value: "materialCostNotes"
				},
				{
					text: "Lead Time",
					value: "leadTimeMinutes"
				},
				{
					text: "Notes",
					value: "notes"
				},
				{
					align: "end",
					sortable: false,
					text: this.isMobile ? "Actions" : "",
					value: "actions"
				}
			];
		}
	},

	methods: {
		genItemStatusStepper( item ) {
			if ( item.$.status === "Unapproved" ) {
				return;
			}

			return <KStatusStepper
				loading={this.$wait.is( "save work order" )}
				modelStatus={item.$.status}
				modelStatuses={item.statuses}
				on={{ "update:modelStatus": status => this.updateStatus( item, status ) }}
				readonly={!this.canEdit}
				statuses={this.statuses}
				title="Mark Work Order As"
			></KStatusStepper>;
		},

		genApproveRejectDialogs( item ) {
			if ( item.$.status !== "Unapproved" || !this.canEdit ) {
				return;
			}

			return [
				<KActionDialog
					confirmButtonColor="success"
					confirmButtonText="Approve"
					loading={this.$wait.is( "save work order" )}
					onConfirm={() => this.updateStatus( item, "Idle" )}
					scopedSlots={{
						default: () => <div>Are you sure you want to approve this work order for <strong>{item.location.name}</strong>?</div>,

						activator: ( { on } ) =>
							<VBtn
								block
								class="btn--text-left mb-2"
								color="success"
								depressed
								large
								on={on}
							>
								<VIcon left>thumb_up</VIcon> Approve
							</VBtn>
					}}
					title="Approve Request"
				></KActionDialog>,

				<KActionDialog
					confirmButtonColor="error"
					confirmButtonText="Reject and Delete"
					loading={this.$wait.is( "delete work order" )}
					onConfirm={() => this.deleteItem( item )}
					scopedSlots={{
						default: () => <div>Are you sure you want to reject this work order for <strong>{item.location.name}</strong>?</div>,

						activator: ( { on } ) =>
							<VBtn
								block
								class="btn--text-left mb-2"
								color="error"
								depressed
								large
								on={on}
							>
								<VIcon left>thumb_down</VIcon> Reject
							</VBtn>
					}}
					title="Reject Request"
				></KActionDialog>
			];
		},

		genItemActions( item ) {
			return <KDataTableMenu closeOnContentClick={false}>
				<VCard dark>
					<VContainer fluid>
						<VRow>
							{item.$.status !== "Unapproved" && <VCol>
								{this.genItemStatusStepper( item )}
							</VCol>}

							<VCol>
								{this.genApproveRejectDialogs( item )}

								<VBtn
									block
									class="btn--text-left mb-2"
									depressed
									large
									to={{
										name: "facilityMaintenanceWorkOrder",
										params: { idWorkOrder: item.id }
									}}
								><VIcon left>visibility</VIcon> View</VBtn>

								{this.canDelete
									&& !item.completed
									&& <WorkOrderDeleteDialog
										item={item}
										onConfirm={() => this.deleteItem( item )}
										scopedSlots={{
											activator: ( { on } ) =>
												<VBtn
													block
													class="btn--text-left"
													depressed
													large
													on={on}
												>
													<VIcon left>delete</VIcon> Delete
												</VBtn>
										}}
									></WorkOrderDeleteDialog>
								}
							</VCol>
						</VRow>
					</VContainer>
				</VCard>
			</KDataTableMenu>;
		},

		updateStatus( item, status ) {
			item.status = status;

			return item.save();
		},

		deleteItem( item ) {
			return item.delete().then( () => {
				this.items = removeByObjectValue( this.items, "id", item.id );
			} );
		},

		async fetchItems() {
			this.items = await this.modifyItemsQuery(
				FacilityMaintenanceWorkOrder
				.select( {
					statuses: [ "created_at", "model_id", "name", "idUserSetBy" ]
				} )
				.include( [
					"location",
					"statuses",
					"statuses.user-set-by",
					"vendor",
					"vendor.vendor-type"
				] )
			)
			.get();
		},

		async fetch() {
			await this.fetchItems();
		}
	},

	render() {
		return <KDataTable
			attrs={this.$props}
			headers={this.$_headers}
			items={this.filteredItems}
			loading={this.$wait.is( "get work orders" )}
			on={{ ...this.$listeners }}
			scopedSlots={{
				"item.cost": ( { item } ) => (
					item.cost
						? formatCurrency( item.cost )
						: null
				),

				"item.materialCost": ( { item } ) => (
					item.materialCost
						? formatCurrency( item.materialCost )
						: null
				),

				"item.actions": ( { item } ) => this.genItemActions( item ),

				...this.$scopedSlots
			}}
		></KDataTable>;
	},

	beforeCreate() {
		delete this.$options.props.items;
		delete this.$options.props.loading;
	}
} );
